import { Col, Icon, Picture, Tappable, Text, useTheme } from "@rally/ui-kit";
import { say } from "../localization/say.ts";
import { useFace } from "../me/face.ts";
import { glyphOf } from "./panel/shape.ts";

const A_MARK_IS = 18;

function ProjectMark({
  name,
  icon,
  image,
  mark,
}: {
  name: string;
  icon?: string | null;
  image?: string | null;
  mark?: string | null;
}) {
  const theme = useTheme();
  const face = useFace(image);
  const src = face ?? mark ?? null;
  if (src) {
    return (
      <Picture
        source={{ uri: src }}
        resizeMode="cover"
        style={{ width: A_MARK_IS, height: A_MARK_IS, borderRadius: theme.radius.xs }}
      />
    );
  }
  if (icon) return <Icon name={glyphOf(icon)} size={16} color="text.secondary" />;
  return (
    <Col
      style={{
        width: A_MARK_IS,
        height: A_MARK_IS,
        borderRadius: theme.radius.xs,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.color["bg.surfaceAlt"],
      }}
    >
      <Text typeName="caption" color="text.secondary" style={{ fontWeight: "600", lineHeight: A_MARK_IS }}>
        {name.trim().charAt(0).toUpperCase()}
      </Text>
    </Col>
  );
}

export function ProjectRow({
  id,
  name,
  icon,
  image,
  mark,
  here,
  onOpen,
  onAdd,
  onInvite,
  expanded,
  onToggle,
}: {
  id: string;
  name: string;
  icon?: string | null;
  image?: string | null;
  mark?: string | null;
  here: boolean;
  onOpen: () => void;
  onAdd: () => void;
  onInvite?: () => void;
  expanded: boolean;
  onToggle?: () => void;
}) {
  const theme = useTheme();
  const short = id.slice(0, 8);
  const action = (
    label: string,
    glyph: "plus" | "user-plus" | "chevron-up" | "chevron-down",
    testID: string,
    onPress: () => void,
    expansion?: boolean,
  ) => (
    <Tappable
      onPress={(event) => {
        event.stopPropagation();
        onPress();
      }}
      accessibilityRole="button"
      accessibilityLabel={label}
      aria-expanded={expansion}
      testID={testID}
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: theme.radius.sm,
      }}
    >
      <Icon name={glyph} size={16} color="text.secondary" />
    </Tappable>
  );
  return (
    <Col
      gap="none"
      testID={`project-row-${id}`}
      style={{
        flexDirection: "row",
        alignItems: "center",
        minHeight: 44,
        backgroundColor: here ? theme.color.accentSoft : "transparent",
        borderRadius: theme.radius.sm,
      }}
    >
      <Tappable
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={name}
        aria-current={here ? "page" : undefined}
        testID={`project-${short}`}
        style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0, borderRadius: theme.radius.sm }}
      />
      <Col
        pointerEvents="none"
        gap={1}
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexShrink: 1,
          minWidth: 0,
          paddingLeft: theme.space[1],
        }}
      >
        <ProjectMark name={name} icon={icon} image={image} mark={mark} />
        <Text
          testID={`project-title-${id}`}
          typeName="body"
          color={here ? "accent" : "text.primary"}
          numberOfLines={1}
          style={{ fontFamily: "var(--rally-brand)", flexShrink: 1 }}
        >
          {name}
        </Text>
      </Col>
      {action(say("chat.newHere"), "plus", `new-in-${short}`, onAdd)}
      {onToggle
        ? action(
            expanded ? say("project.collapse") : say("project.expand"),
            expanded ? "chevron-up" : "chevron-down",
            `project-more-${id}`,
            onToggle,
            expanded,
          )
        : null}
      {onInvite ? action(say("chat.inviteHere"), "user-plus", `invite-to-${short}`, onInvite) : null}
    </Col>
  );
}
